import { getParentColor, getParentColorById } from "@/lib/parentColor";

// v77.19 — ParentBadge: a small, reusable visual marker for any parent
// across the app. Renders a coloured dot + the parent's name (or just
// the initial in compact mode). Always reads colour from the persisted
// `parent_color` field, so every appearance of a given parent renders
// consistently — paid_by chips, sender avatars, schedule legends, etc.
//
// Usage:
//   <ParentBadge member={member} />
//   <ParentBadge userId="abc" members={members} />
//   <ParentBadge member={m} variant="dot" />          // dot only
//   <ParentBadge member={m} variant="initial" />      // dot + initial circle
//   <ParentBadge member={m} variant="full" />         // dot + full name (default)
//   <ParentBadge member={m} className="text-xs" />

export function ParentBadge({
  member = null,
  userId = null,
  members = null,
  variant = "full",
  className = "",
  testId,
}) {
  const resolved = member || (userId && (members || []).find((m) => m && m.id === userId)) || null;
  const color = resolved ? getParentColor(resolved) : getParentColorById(userId, members);
  const name = (resolved && resolved.name) || "—";
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const testProps = testId ? { "data-testid": testId } : {};

  if (variant === "dot") {
    return (
      <span
        className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${className}`}
        style={{ backgroundColor: color }}
        title={name}
        {...testProps}
      />
    );
  }

  if (variant === "initial") {
    return (
      <span
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold text-white flex-shrink-0 ${className}`}
        style={{ backgroundColor: color }}
        title={name}
        {...testProps}
      >
        {initial}
      </span>
    );
  }

  // full (default) — dot + name. Inherits font size from parent.
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${className}`}
      {...testProps}
    >
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="font-medium text-[#2E3A3F] truncate">{name}</span>
    </span>
  );
}

export default ParentBadge;
